/**
 * Ad Form Defaults — per-user "remember last used" helper for the
 * post-ad form (category + location only).
 *
 * Same anti-leak rules as the rest of the app:
 *   • Versioned key `wassla.adFormDefaults.v2.<userId>`.
 *   • ALL reads/writes require a real, non-empty userId. Empty strings,
 *     `"guest"`, null, undefined are rejected (no-op writes, null read).
 *   • Anonymous users do NOT persist defaults to disk.
 *   • Legacy prefix sweep + full v2 purge helpers for AuthContext.
 *
 * This store is ONLY written after a successful publish, so it reflects
 * actually-used values — not abandoned drafts.
 */

const PREFIX_V2 = 'wassla.adFormDefaults.v2.';
const LEGACY_PREFIX = 'wassla.adFormDefaults.v1.';

function isValidUserId(userId) {
  if (typeof userId !== 'string') return false;
  const trimmed = userId.trim();
  if (!trimmed) return false;
  if (trimmed === 'guest') return false;
  return true;
}

function getKey(userId) {
  return isValidUserId(userId) ? `${PREFIX_V2}${userId}` : null;
}

export function sweepLegacyAdFormDefaultKeys() {
  if (typeof window === 'undefined') return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(LEGACY_PREFIX) && !k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

export function purgeAllAdFormDefaults() {
  if (typeof window === 'undefined') return;
  try {
    sweepLegacyAdFormDefaultKeys();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX_V2)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

/** Returns `{ category, location }` or `null`. Never throws. */
export function getAdFormDefaults(userId) {
  if (typeof window === 'undefined') return null;
  if (!isValidUserId(userId)) return null;
  const key = getKey(userId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const category = typeof parsed.category === 'string' ? parsed.category : '';
    const location = typeof parsed.location === 'string' ? parsed.location : '';
    if (!category && !location) return null;
    return { category, location };
  } catch {
    return null;
  }
}

/**
 * Save `{ category, location }` for the given user. Anonymous users are
 * silently ignored. Blank values are dropped (we only persist real
 * usage signals).
 */
export function saveAdFormDefaults(defaults, userId) {
  if (!isValidUserId(userId)) return;
  const key = getKey(userId);
  if (!key) return;
  const category = typeof defaults?.category === 'string' ? defaults.category.trim() : '';
  const location = typeof defaults?.location === 'string' ? defaults.location.trim() : '';
  if (!category && !location) return;
  try {
    localStorage.setItem(key, JSON.stringify({ category, location }));
  } catch {
    /* ignore */
  }
}
